import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ArrowRight, X, ChevronLeft, ChevronRight } from "lucide-react";
import { useReveal } from "@/hooks/useReveal";
import { useContent } from "@/context/ContentContext";
import { resolveMedia } from "@/lib/api";

export default function ProjectsBlock() {
  const { content } = useContent();
  const projects = content.projects || [];
  const page = content.projectsPage || {};
  const [active, setActive] = useState(null);
  const [lightbox, setLightbox] = useState(null); // { images, index }
  const headRef = useReveal();
  const { hash } = useLocation();

  // Keyboard nav for lightbox + body scroll lock
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") setLightbox((lb) => lb && ({ ...lb, index: (lb.index + 1) % lb.images.length }));
      if (e.key === "ArrowLeft") setLightbox((lb) => lb && ({ ...lb, index: (lb.index - 1 + lb.images.length) % lb.images.length }));
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [lightbox]);

  // Slinkti į atitinkamą sklypą, jei URL turi #slug
  useEffect(() => {
    if (!hash) return;
    const id = hash.replace("#", "");
    const el = document.getElementById(`project-${id}`);
    if (el) {
      setTimeout(() => {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    }
  }, [hash]);

  return (
    <section data-testid="projects-section" className="bg-[#f9f8f6]">
      {/* Antraštė */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 pt-[140px] md:pt-[180px] pb-12 md:pb-24">
        <div className="reveal grid grid-cols-1 md:grid-cols-12 gap-8 items-end" ref={headRef}>
          <div className="md:col-span-7">
            <span className="overline">— 02 / Projektai</span>
            <h1 className="mt-6 font-serif-display text-4xl md:text-6xl lg:text-7xl leading-[1.04] tracking-tight text-[#1a1a1a]">
              {page.headlineTop || "Skirtingos pirtys."}
              <br />
              <span className="italic">{page.headlineBottom || "Viena filosofija."}</span>
            </h1>
          </div>
          <div className="md:col-span-5 md:text-right">
            <p className="text-[#3a3a3a] text-base md:text-lg leading-relaxed font-light max-w-md md:ml-auto">
              {page.intro || "Keturi pirties modeliai. Visi kuriami su tuo pačiu architektūriniu požiūriu ir asmenišku dėmesiu detalėms."}
            </p>
          </div>
        </div>
      </div>

      {/* Sklypai – kiekvienas projektas savo plačia sekcija */}
      {projects.map((p, i) => (
        <ProjectBlock
          key={p.id}
          project={p}
          reverse={i % 2 === 1}
          onOpen={() => setActive(p)}
        />
      ))}

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent
          data-testid="project-modal"
          className="max-w-5xl w-[96vw] p-0 bg-[#f9f8f6] border border-[#e5e2dd] rounded-none overflow-hidden max-h-[92vh] overflow-y-auto"
        >
          {active && (
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="bg-[#efedea] flex items-center justify-center">
                <div className="w-full overflow-hidden">
                  <img
                    src={resolveMedia(active.cover)}
                    alt={active.name}
                    className="w-full h-auto object-contain cursor-zoom-in"
                    onClick={() => {
                      const imgs = (active.gallery && active.gallery.length ? active.gallery : [active.cover]).filter(Boolean);
                      setActive(null);
                      setLightbox({ images: imgs, index: 0 });
                    }}
                  />
                </div>
              </div>
              <div className="p-8 md:p-12 relative">
                <button
                  data-testid="project-modal-close"
                  onClick={() => setActive(null)}
                  className="absolute top-4 right-4 p-2 text-[#1a1a1a] hover:text-[#a66e4b]"
                  aria-label="Uždaryti"
                >
                  <X size={20} strokeWidth={1.5} />
                </button>
                <span className="overline">{active.label}</span>
                <DialogTitle asChild>
                  <h3 className="mt-4 font-serif-display text-3xl md:text-4xl leading-tight text-[#1a1a1a]">
                    {active.name} <span className="text-[#a66e4b] italic">— {active.area}</span>
                  </h3>
                </DialogTitle>
                <div className="mt-2 text-sm text-[#5c5c5c] tracking-wide italic">
                  {active.idea}
                </div>

                <DialogDescription asChild>
                  <p className="mt-6 text-[#3a3a3a] text-base leading-relaxed font-light">
                    {active.intro}
                  </p>
                </DialogDescription>

                {Array.isArray(active.sections) && active.sections.length > 0 ? (
                  <div className="mt-8 space-y-7">
                    {active.sections.map((sec, sidx) => (
                      <div key={sidx}>
                        <h4 className="font-serif-display italic text-[#a66e4b] text-lg md:text-xl mb-3">
                          {sec.title}
                        </h4>
                        <ul className="space-y-2">
                          {(sec.items || []).map((it, iidx) => (
                            <li
                              key={iidx}
                              className={`flex gap-3 text-[14px] text-[#3a3a3a] font-light leading-relaxed ${
                                it.startsWith("—") ? "pl-6" : ""
                              }`}
                            >
                              {!it.startsWith("—") && (
                                <span className="mt-[10px] inline-block w-3 h-px bg-[#a66e4b] shrink-0" />
                              )}
                              <span>{it.replace(/^—\s*/, "")}</span>
                            </li>
                          ))}
                        </ul>
                        {sec.note && (
                          <p className="mt-3 text-xs italic text-[#5c5c5c] font-light leading-relaxed">
                            {sec.note}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <ul className="mt-6 space-y-3">
                    {(active.bullets || []).map((b) => (
                      <li
                        key={b}
                        className="flex gap-3 text-sm text-[#3a3a3a] font-light leading-relaxed"
                      >
                        <span className="mt-2 inline-block w-3 h-px bg-[#a66e4b] shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {active.important && (
                  <div className="mt-10 border-t border-[#e5e2dd] pt-6">
                    <span className="overline">Svarbu</span>
                    <p className="mt-3 text-xs text-[#5c5c5c] font-light leading-relaxed italic">
                      {active.important}
                    </p>
                  </div>
                )}

                {active.plan && (
                  <div className="mt-10 border-t border-[#e5e2dd] pt-6">
                    <span className="overline">Planas</span>
                    <button
                      type="button"
                      onClick={() => {
                        setActive(null);
                        setLightbox({ images: [active.plan], index: 0 });
                      }}
                      className="mt-4 block w-full bg-[#efedea] p-4 cursor-zoom-in"
                    >
                      <img
                        src={resolveMedia(active.plan)}
                        alt={`${active.name} planas`}
                        className="w-full h-auto object-contain"
                        loading="lazy"
                      />
                    </button>
                  </div>
                )}

                <div className="mt-10 grid grid-cols-3 gap-3">
                  {(active.gallery || []).map((g, idx) => (
                    <button
                      type="button"
                      key={idx}
                      onClick={() => {
                        const imgs = (active.gallery || []).filter(Boolean);
                        setActive(null);
                        setLightbox({ images: imgs, index: idx });
                      }}
                      className="aspect-square overflow-hidden bg-[#efedea] group cursor-zoom-in"
                    >
                      <img
                        src={resolveMedia(g)}
                        alt={`${active.name} – nuotrauka ${idx + 1} Klaipėdoje`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </button>
                  ))}
                </div>

                <div className="mt-10 flex items-center justify-between border-t border-[#e5e2dd] pt-6">
                  <Link
                    to="/kontaktai"
                    onClick={() => setActive(null)}
                    data-testid="project-modal-cta"
                    className="text-[12px] uppercase tracking-[0.22em] text-[#1a1a1a] border-b border-[#1a1a1a] pb-1 hover:text-[#a66e4b] hover:border-[#a66e4b]"
                  >
                    Klausti dėl modelio
                  </Link>
                  <span className="overline">{active.priceFrom}</span>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {lightbox && (
        <div
          data-testid="lightbox"
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
          onClick={() => setLightbox(null)}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            className="absolute top-6 right-6 p-2 text-white/80 hover:text-white z-10"
            aria-label="Uždaryti"
            data-testid="lightbox-close"
          >
            <X size={28} strokeWidth={1.25} />
          </button>
          {lightbox.images.length > 1 && (
            <>
              <button
                type="button"
                data-testid="lightbox-prev"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightbox((lb) => ({ ...lb, index: (lb.index - 1 + lb.images.length) % lb.images.length }));
                }}
                className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 p-3 text-white/70 hover:text-white z-10"
                aria-label="Ankstesnė"
              >
                <ChevronLeft size={40} strokeWidth={1.25} />
              </button>
              <button
                type="button"
                data-testid="lightbox-next"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightbox((lb) => ({ ...lb, index: (lb.index + 1) % lb.images.length }));
                }}
                className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 p-3 text-white/70 hover:text-white z-10"
                aria-label="Kita"
              >
                <ChevronRight size={40} strokeWidth={1.25} />
              </button>
            </>
          )}
          <img
            src={resolveMedia(lightbox.images[lightbox.index])}
            alt={`Karkasinė pirtis Klaipėdoje – nuotrauka ${lightbox.index + 1}`}
            className="max-w-[92vw] max-h-[88vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          {lightbox.images.length > 1 && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-xs tracking-[0.32em] uppercase">
              {lightbox.index + 1} / {lightbox.images.length}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function ProjectBlock({ project, reverse, onOpen }) {
  const ref = useReveal();
  return (
    <article
      id={`project-${project.slug}`}
      data-testid={`project-block-${project.id}`}
      className="border-t border-[#e5e2dd]"
    >
      <div
        ref={ref}
        className="reveal max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center"
      >
        <div
          className={`md:col-span-7 ${
            reverse ? "md:order-2" : ""
          }`}
        >
          <div className="img-zoom w-full bg-[#efedea]">
            <img
              src={resolveMedia(project.cover)}
              alt={project.name}
              className="w-full h-auto object-contain"
              loading="lazy"
            />
          </div>
        </div>
        <div className={`md:col-span-5 ${reverse ? "md:order-1" : ""}`}>
          <div className="flex items-center gap-4 mb-6">
            <span className="overline">{project.label}</span>
            <span className="w-8 h-px bg-[#a66e4b]" />
            <span className="overline text-[#a66e4b]">{project.area}</span>
          </div>
          <h2 className="font-serif-display text-4xl md:text-5xl leading-tight tracking-tight text-[#1a1a1a]">
            {project.name}
          </h2>
          <p className="mt-3 italic text-[#5c5c5c] font-light">
            {project.tagline}
          </p>
          <p className="mt-6 text-[#3a3a3a] text-base md:text-lg leading-relaxed font-light">
            {project.intro}
          </p>

          <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            {project.bullets.slice(0, 6).map((b) => (
              <li
                key={b}
                className="flex gap-3 text-[14px] text-[#3a3a3a] font-light leading-relaxed"
              >
                <span className="mt-2 inline-block w-3 h-px bg-[#a66e4b] shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>

          <div className="mt-10 flex flex-wrap items-center gap-6">
            <button
              type="button"
              data-testid={`project-view-${project.id}`}
              onClick={onOpen}
              className="btn-primary"
            >
              Visa informacija <ArrowRight size={16} strokeWidth={1.5} />
            </button>
            <Link
              to="/kainos"
              className="text-[12px] uppercase tracking-[0.22em] text-[#1a1a1a] border-b border-[#1a1a1a] pb-1 hover:text-[#a66e4b] hover:border-[#a66e4b]"
            >
              {project.priceFrom}
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
}
